import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HoverMenu } from '@/components/common/HoverMenu';
import DesktopMenuLayout from '@/components/layout/navbar/desktop/DesktopMenuLayout';
import NavBarDropdown from '@/components/layout/navbar/desktop/NavBarDropdown';
import Search from '@/components/layout/navbar/Search';
import { useAppDispatch } from '@/redux';
import { desktopMenuOpened } from '@/redux/slices/gui.slice';
import styles from './NavBarDesktop.module.scss';

const DELAY_ENTER = 200;

type NavBarDesktopProps = {
	navBarMenu?: any[];
	onClickMenu: (arg: any) => void;
};

export const NavBarDesktop = ({ navBarMenu, onClickMenu }: NavBarDesktopProps): React.JSX.Element => {
	const [isHoverMenuTimerTriggered, setIsHoverMenuTimerTriggered] = useState();
	const [activeMenu, setActiveMenu] = useState(undefined);
	const [menuOpen, setMenuOpen] = useState(false);
	const dispatch = useAppDispatch();
	const router = useRouter();
	const navbar = useRef<any>();

	const closeDropdown = useCallback(() => {
		setMenuOpen(false);
	}, []);

	const openDropDown = useCallback(() => {
		setMenuOpen(true);
	}, []);

	const onSelectMenu = useCallback((menu) => {
		setMenuOpen(true);
		setActiveMenu(menu);
	}, []);

	const handleActionWhenOpen = useCallback(() => {
		dispatch(desktopMenuOpened({ opened: true }));
	}, []);

	const handleActionWhenClose = useCallback(() => {
		dispatch(desktopMenuOpened({ opened: false }));
		setActiveMenu(undefined);
	}, []);

	const onWindowClick = useCallback(
		(e) => {
			if (navbar.current && !navbar.current.contains(e.target)) {
				closeDropdown();
			}
		},
		[navbar],
	);

	useEffect(() => {
		router.events.on('routeChangeStart', closeDropdown);
		window.addEventListener('click', onWindowClick);

		return () => {
			router.events.off('routeChangeStart', closeDropdown);
			window.removeEventListener('click', onWindowClick);
		};
	}, [router.events]);

	useEffect(() => {
		setActiveMenu(undefined);
	}, [router.route]);

	return navBarMenu ? (
		<nav
			className={`navbar ${styles['desktop']} navbar-expand-lg`}
			ref={navbar}
			onClick={closeDropdown}
			onMouseLeave={closeDropdown}
			data-test-id="navbar-desktop"
			data-rbf="navbar-desktop"
		>
			<div className={styles['millesima-navbar']}>
				<div className={`row ${styles['no-row-margin']}`}>
					<div className={`container ${styles['container-width']}`}>
						<div className={styles['millesima-menu']}>
							<div className={styles['menu-container']}>
								<NavBarDropdown
									isOpen={menuOpen ? true : isHoverMenuTimerTriggered}
									navBarMenu={navBarMenu}
									activeMenu={activeMenu}
									onSelectMenu={onSelectMenu}
									onClickMenu={onClickMenu}
								/>
								<div className={styles['navbar-search']}>
									<Search />
								</div>
								<HoverMenu
									shouldOpen={menuOpen}
									actionWhenOpen={handleActionWhenOpen}
									actionWhenClose={handleActionWhenClose}
									closeWhenClickOutside={true}
									delayEnter={DELAY_ENTER}
									setIsTimerTriggered={setIsHoverMenuTimerTriggered}
								>
									{navBarMenu.map((item, i) => (
										<DesktopMenuLayout
											navBarMenu={item}
											activeParentMenu={activeMenu}
											key={i}
											onMouseEnter={openDropDown}
											onClickMenu={onClickMenu}
										/>
									))}
								</HoverMenu>
							</div>
						</div>
					</div>
				</div>
			</div>
		</nav>
	) : null;
};

export default React.memo(NavBarDesktop);
